import {
  useState,
  useEffect,
} from 'react';
import {
  Box,
  Typography,
} from '@mui/material';

import { v4 as uuidv4 } from 'uuid';

import { useTranslation } from 'react-i18next';

import { DetailedInfoContainer } from '../../../ModalComponent/ModalBody/DetailedInfoContainer/DetailedInfoContainer';
import { ModalBodyInfo } from '../../../ModalComponent/ModalBody/ModalBodyInfo/ModalBodyInfo';
import { ModalBodyTitle } from '../../../ModalComponent/ModalBody/ModalBodyTitle/ModalBodyTitle';
import { NoInfoBox } from '../../../ModalComponent/ModalBody/NoInfoBox/NoInfoBox';

import { namespaces } from '../../../../../../../../../i18n/i18n.constants';
import { ObjectType } from '../../../../../../../../../types/objectType';

import { civilWorksBasicEnumerationStyle } from './civilWorksBasicEnumerationStyle';

const measurementNumberFormatter = new Intl.NumberFormat('es-ES', {
  maximumFractionDigits: 2,
});

const formatMeasurement = (value: number | string) => {
  const parsedValue = Number(typeof value === 'string' ? value.replace(',', '.') : value);
  return Number.isFinite(parsedValue) ? measurementNumberFormatter.format(parsedValue) : value;
};

export const CivilWorksBasicEnumeration = (
  {
    modalDetailedInfo,
    enumerationType,
    descriptionWithVolumen,
  }: ObjectType,
) => {
  const [enumerationParsedInfo, setEnumerationParsedInfo] = useState<ObjectType[]>([]);
  const { t } = useTranslation(namespaces.pages.resumesSearch);
  const {
    title,
    bbddKey,
  } = enumerationType;
  const {
    main,
    code,
    description,
    descriptionCode,
    amount,
  } = bbddKey;
  const TRADUCTION_BASE_PATH = `detailedInfoModal.civilWorksDetailedInfo.${title}.`;

  useEffect(() => {
    const enumerationInfo = [...modalDetailedInfo[main]];
    if (enumerationInfo.length > 0) {
      const orderedInfo = title === 'units'
        ? enumerationInfo.sort((a: ObjectType, b: ObjectType) => (
          a[code].toString().localeCompare(b[code].toString(), undefined, { numeric: true })
        ))
        : enumerationInfo.sort((a: ObjectType, b: ObjectType) => (
          b[code].toString().length - a[code].toString().length
        ));
      const maxLengthCode = title === 'magnitudes'
        ? 0
        : Math.max(...orderedInfo.map((item: ObjectType) => item[code].toString().length));
      const parsedInfo = orderedInfo.map((item: ObjectType) => {
        if (title === 'magnitudes') return { ...item };
        return {
          ...item,
          [code]: item[code].toString().padStart(maxLengthCode, '0'),
        };
      });
      setEnumerationParsedInfo(parsedInfo);
    } else setEnumerationParsedInfo([]);
  }, [modalDetailedInfo, main, code, title]);
  const { sx } = civilWorksBasicEnumerationStyle;
  return (
    <DetailedInfoContainer>
      <ModalBodyTitle value={t(`${TRADUCTION_BASE_PATH}title`)} />
      <ModalBodyInfo >
        {
          enumerationParsedInfo.length > 0
            ? enumerationParsedInfo.map((item: ObjectType, index: number) => {
              const arrayLength = enumerationParsedInfo.length - 1;
              return (<Box
                sx={
                  (arrayLength !== index)
                    ? sx.detailContainer
                    : sx.detailContainerLastItem
                }
                key={uuidv4()}
              >
                <Box
                  sx={
                    descriptionWithVolumen
                      ? sx.codeDescriptionWithVolume
                      : sx.codeDescription
                  }
                >
                  <Typography
                    variant='subtitle2'
                    p={0}
                    mb={0}
                  >
                    {item[code]}
                  </Typography>
                  <Typography
                    variant='subtitle2'
                    p={0}
                    ml={descriptionWithVolumen ? 2 : 3}
                    mb={0}
                    sx={sx.enumerationDescription}
                  >
                    {item[description]}
                  </Typography>
                </Box>
                {
                  descriptionWithVolumen
                  && (<>
                    <Box sx={sx.magnitudeCode}>
                      <Typography
                        variant='body2'
                        color='text.secondary'
                        p={0}
                        mb={0}
                      >
                        {item[descriptionCode]}
                      </Typography>
                    </Box>
                    <Box sx={sx.amount}>
                      <Typography
                        variant='subtitle2'
                        p={0}
                        ml={2}
                        mb={0}
                        sx={sx.subtitle2}
                      >
                        {
                          item[amount]
                            ? formatMeasurement(item[amount])
                            : null
                        }
                      </Typography>
                    </Box>
                  </>)
                }
              </Box>);
            })
            : <NoInfoBox
              value={t(`${TRADUCTION_BASE_PATH}noInfoText`)}
            />
        }
      </ModalBodyInfo>
    </DetailedInfoContainer >
  );
};
